import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import type { Business } from "../../data/discounts"
import apiClient from "../../api/apiClient"
import { serializeFirestoreData } from "../../utils/timestamp"

interface DiscountsState {
  businesses: Business[]
  isLoading: boolean
  error: string | null
}

const initialState: DiscountsState = {
  businesses: [],
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchBusinesses = createAsyncThunk(
  "discounts/fetchBusinesses",
  async (category: string | undefined, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/discounts${category && category !== "all" ? `?category=${category}` : ""}`)

      // Serialize any Firestore Timestamp objects in the response
      const serializedBusinesses = response.data.businesses.map((business: any) => serializeFirestoreData(business))

      return serializedBusinesses
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch businesses")
    }
  },
)

export const getBusinessById = createAsyncThunk(
  "discounts/getBusinessById",
  async (businessId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/discounts/${businessId}`)
      return serializeFirestoreData(response.data.business)
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to get business")
    }
  },
)

export const addBusiness = createAsyncThunk(
  "discounts/addBusiness",
  async (
    {
      name,
      category,
      address,
      phone,
      discount,
      buildings,
      logo,
    }: {
      name: string
      category: string
      address: string
      phone: string
      discount: string
      buildings: string[]
      logo?: File
    },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData()
      formData.append("name", name)
      formData.append("category", category)
      formData.append("address", address)
      formData.append("phone", phone)
      formData.append("discount", discount)
      formData.append("isActive", "true") // Default to active when creating

      buildings.forEach((building) => {
        formData.append("buildings", building)
      })

      if (logo) {
        formData.append("logo", logo)
      }

      const response = await apiClient.post("/discounts", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      return serializeFirestoreData(response.data.business)
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to add business")
    }
  },
)

export const updateBusiness = createAsyncThunk(
  "discounts/updateBusiness",
  async (
    {
      id,
      name,
      category,
      address,
      phone,
      discount,
      buildings,
      logo,
      isActive,
    }: {
      id: string
      name: string
      category: string
      address: string
      phone: string
      discount: string
      buildings: string[]
      logo?: File
      isActive?: boolean
    },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData()
      formData.append("name", name)
      formData.append("category", category)
      formData.append("address", address)
      formData.append("phone", phone)
      formData.append("discount", discount)

      // Only include isActive if it's explicitly provided
      if (isActive !== undefined) {
        formData.append("isActive", String(isActive))
      }

      buildings.forEach((building) => {
        formData.append("buildings", building)
      })

      if (logo) {
        formData.append("logo", logo)
      }

      const response = await apiClient.put(`/discounts/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      // Make sure to include the id and isActive in the returned object
      return {
        ...serializeFirestoreData(response.data.business || {}),
        id,
        isActive: isActive !== undefined ? isActive : true,
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to update business")
    }
  },
)

export const toggleBusinessStatus = createAsyncThunk(
  "discounts/toggleBusinessStatus",
  async ({ id, isActive }: { id: string; isActive: boolean }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/discounts/${id}/status`, { isActive })
      return { id, isActive }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to update business status")
    }
  },
)

export const deleteBusiness = createAsyncThunk(
  "discounts/deleteBusiness",
  async (businessId: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/discounts/${businessId}`)
      return businessId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to delete business")
    }
  },
)

const discountsSlice = createSlice({
  name: "discounts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch businesses
      .addCase(fetchBusinesses.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchBusinesses.fulfilled, (state, action: PayloadAction<Business[]>) => {
        state.businesses = action.payload
        state.isLoading = false
        state.error = null
      })
      .addCase(fetchBusinesses.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Get business by ID
      .addCase(getBusinessById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getBusinessById.fulfilled, (state, action) => {
        const index = state.businesses.findIndex((business) => business.id === action.payload.id)
        if (index !== -1) {
          state.businesses[index] = action.payload
        } else {
          state.businesses.push(action.payload)
        }
        state.isLoading = false
        state.error = null
      })
      .addCase(getBusinessById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Add business
      .addCase(addBusiness.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(addBusiness.fulfilled, (state, action: PayloadAction<Business>) => {
        state.businesses.push(action.payload)
        state.isLoading = false
        state.error = null
      })
      .addCase(addBusiness.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Update business
      .addCase(updateBusiness.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateBusiness.fulfilled, (state, action) => {
        const index = state.businesses.findIndex((business) => business.id === action.payload.id)
        if (index !== -1) {
          state.businesses[index] = action.payload as Business
        }
        state.isLoading = false
        state.error = null
      })
      .addCase(updateBusiness.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Toggle business status
      .addCase(toggleBusinessStatus.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(toggleBusinessStatus.fulfilled, (state, action) => {
        const { id, isActive } = action.payload
        const index = state.businesses.findIndex((business) => business.id === id)
        if (index !== -1) {
          state.businesses[index].isActive = isActive
        }
        state.isLoading = false
        state.error = null
      })
      .addCase(toggleBusinessStatus.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Delete business
      .addCase(deleteBusiness.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteBusiness.fulfilled, (state, action) => {
        state.businesses = state.businesses.filter((business) => business.id !== action.payload)
        state.isLoading = false
        state.error = null
      })
      .addCase(deleteBusiness.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export default discountsSlice.reducer
